import { useCallback, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { useRealtime, sfx } from "../lib/realtime";
import { Button } from "../components/ui/button";
import { Target, Loader2, EyeOff } from "lucide-react";
import { toast } from "sonner";

const DIFF = [
  { id: "easy", label: "Facile", color: "#10b981", dot: "🟢" },
  { id: "medium", label: "Media", color: "#f59e0b", dot: "🟠" },
  { id: "hard", label: "Difficile", color: "#ef4444", dot: "🔴" },
];

export default function ObjectivesGenerator() {
  const [difficulty, setDifficulty] = useState("easy");
  const [result, setResult] = useState(null);
  const [drawing, setDrawing] = useState(false);

  useRealtime(useCallback((ev) => {
    if (ev.type === "OBJECTIVE_DRAW") {
      setResult({ title: ev.title, description: ev.description, difficulty: ev.difficulty });
      setDrawing(false);
      sfx.result(0.6);
    }
  }, []));

  const draw = async () => {
    setDrawing(true);
    setResult(null);
    try {
      sfx.ensure();
      sfx.start(0.5);
      await api.drawObjective(difficulty);
    } catch (e) {
      setDrawing(false);
      toast.error(e.response?.data?.detail || "Errore estrazione obiettivo");
    }
  };

  const active = DIFF.find((d) => d.id === difficulty);

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <PageHeader title="Obiettivi" subtitle="Estrai una challenge casuale in base alla difficoltà scelta." accent="amber" />

      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
        {DIFF.map((d) => (
          <button
            key={d.id}
            data-testid={`objective-difficulty-${d.id}`}
            onClick={() => setDifficulty(d.id)}
            className={`rounded-xl p-5 border-2 transition-colors text-center ${
              difficulty === d.id ? "bg-[#181d2a]" : "bg-[#131722] hover:bg-[#181d2a]"
            }`}
            style={{ borderColor: difficulty === d.id ? d.color : "#334155" }}
          >
            <div className="text-2xl mb-1">{d.dot}</div>
            <div className="font-display font-extrabold uppercase text-lg md:text-2xl tracking-wide" style={{ color: d.color }}>
              {d.label}
            </div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          data-testid="draw-objective-button"
          onClick={draw}
          disabled={drawing}
          className="h-14 px-10 text-lg font-display font-bold uppercase tracking-wide text-[#080a0f] bg-amber-500 hover:bg-amber-400"
        >
          {drawing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Target className="w-5 h-5 mr-2" />}
          Estrai obiettivo
        </Button>
        <Button variant="outline" onClick={() => api.overlayHide()} className="h-14 px-5 border-slate-600 text-slate-200 hover:bg-[#181d2a]" data-testid="objective-hide-overlay">
          <EyeOff className="w-4 h-4 mr-2" /> Nascondi overlay
        </Button>
      </div>

      {result && (
        <div
          data-testid="objective-result"
          className="mt-8 bg-[#131722] border-2 rounded-xl p-8 animate-sw-pop"
          style={{ borderColor: active.color }}
        >
          <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: active.color }}>
            {active.dot} Obiettivo {active.label}
          </div>
          <div className="font-display text-4xl md:text-5xl font-black uppercase text-white leading-tight">
            {result.title}
          </div>
          <p className="text-slate-300 text-lg mt-3">{result.description}</p>
        </div>
      )}
    </div>
  );
}
